import {metrics} from './utils/metrics.js';
import {hasOrbitalPosition} from './utils/orbitalCamera.js';
import {tonightPasses, selectTonightPass} from './utils/tonightPasses.js';
import AuroraOutlook from './components/AuroraOutlook.jsx';
import ObjectDetails from './components/ObjectDetails.jsx';
import {DEFAULT_EVENT_FILTERS} from './utils/eventFeed.js';
import { OrbitalGlobe, OrbitsView, TonightMap, GearRecipe, PassPlot, SkyPlotView } from './components/DeferredViews.jsx';
import RendererBoundary from './components/RendererBoundary.jsx';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import SolarBriefing from './components/SolarBriefing.jsx';
import SolarHero from './components/SolarHero.jsx';
import SolarRightPanel from './components/SolarRightPanel.jsx';
import SolarForecastStrip from './components/SolarForecastStrip.jsx';
import Ticker from './components/Ticker.jsx';
import MobileTopBar from './components/MobileTopBar.jsx';
import DestinationNav from './components/DestinationNav.jsx';
import './shell.css';
import MobileSheet from './components/MobileSheet.jsx';
import MobilePaneEvents from './components/MobilePaneEvents.jsx';
import MobilePaneSelection from './components/MobilePaneSelection.jsx';
import MobilePaneLayers from './components/MobilePaneLayers.jsx';
import MobilePaneTonight from './components/MobilePaneTonight.jsx';
import MobilePaneMore from './components/MobilePaneMore.jsx';
import LeftConsole from './components/LeftConsole.jsx';
import RightConsole from './components/RightConsole.jsx';
import LaunchTimeline from './components/LaunchTimeline.jsx';
import CommandPalette from './components/CommandPalette.jsx';
import TonightDetails from './components/TonightDetails.jsx';
import TonightStrip from './components/TonightStrip.jsx';
import { useTonight } from './hooks/useTonight.js';
import { useNotification } from './hooks/useNotification.js';
import { DEFAULT_CATEGORIES } from './components/FilterTray.jsx';
import EventBusProvider from './components/EventBusProvider.jsx';
import { useEventBus } from './hooks/useEventBus.js';
import { useObserver } from './hooks/useObserver.js';
import { useSpaceWeather, useConjunctions, useCmes } from './hooks/usePhase3b.js';
import { useReentry } from './hooks/usePhase3.js';
import { useIsMobile, useViewport } from './hooks/useIsMobile.js';
import LocationDialog from './components/LocationDialog.jsx';
import { useSolarBriefing } from './hooks/useSolarBriefing.js';

// Shared location, alerts and reading preferences.
import AlertToast from './components/AlertToast.jsx';
import DisplayPreferences from './components/DisplayPreferences.jsx';
import PopoutModal from './components/PopoutModal.jsx';
import {useDisplayPreferences} from './hooks/useDisplayPreferences.js';
import {useNightVision} from './hooks/useNightVision.js';

const DEFAULT_FILTERS = {
  categories: DEFAULT_CATEGORIES,
  alt_min: 0, alt_max: 42000,
  sunlit_only: false, visible_only: false,
  time_offset_sec: 0,
};

const ROOT  = { position: 'fixed', top: 'env(safe-area-inset-top, 0px)', left: 0, right: 0, bottom: 0,
                overflow: 'hidden', background: '#000005' };
function AppInner() {
  const isMobile = useIsMobile();
  const viewport = useViewport();
  const short = viewport.height < 500;
  const [eventFilters, setEventFilters] = useState(DEFAULT_EVENT_FILTERS);
  const [alertHeight, setAlertHeight] = useState(0);
  const [locationOpen, setLocationOpen] = useState(false);
  const [display,setDisplay]=useDisplayPreferences();
  const [displayOpen,setDisplayOpen]=useState(false),[layersOpen,setLayersOpen]=useState(false);
  useNightVision(); // all chart/DOM colors update with either night-mode control

  const [filters,      setFilters]      = useState(DEFAULT_FILTERS);
  const selectionGeneration = useRef(0);
  const [selected,     setSelected]     = useState(null);
  const [tracked,      setTracked]      = useState(null);
  const [searchTarget, setSearchTarget] = useState(null);
  const [dataHealth, setDataHealth] = useState(null);
  const [dim,          setDim]          = useState('3d'); // '3d' globe | '2d' lanes | 'solar' heliograph
  const [tonightActivity, setTonightActivity] = useState('iss'); // open Tonight on the satellite all-sky dome (next pass auto-selected)
  // Which pass is plotted in the Tonight all-sky view. Falls back to the best
  // upcoming pass; resets when the observer moves.
  const [focusedPass,  setFocusedPass]  = useState(null);
  const [tonightCenter, setTonightCenter] = useState('sky'); // 'sky' (PassPlot) | 'map' (TonightMap)
  const [solarEvent,   setSolarEvent]   = useState(null); // Event surfaced in right panel during solar mode

  // Contextual panel state survives viewport changes.
  const [activeTab,   setActiveTab]   = useState(null);
  const [pinnedOverview, setPinnedOverview] = useState(false);
  const exploreDim = useRef('3d');
  const panelTrigger = useRef(null);
  const toolsTrigger = useRef(null);
  const focusPanel = useRef(false);
  const [exploreToolsOpen, setExploreToolsOpen] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [paletteOpenCount, setPaletteOpenCount] = useState(0);

  const prevSelectedRef = useRef(null);
  useEffect(() => {
    if (selected && !tracked && (selected.norad_id ?? selected.type ?? selected.name) !== (prevSelectedRef.current?.norad_id ?? prevSelectedRef.current?.type ?? prevSelectedRef.current?.name)) {
      setActiveTab('selection');
      setSheetExpanded(true);
    }
    prevSelectedRef.current = selected;
  }, [selected, tracked]);

  const { push } = useEventBus();
  const { observer, setObserver, passes, visibleNow, visibleList, loading: passesLoading, error: passesError } = useObserver();
  const { weather }      = useSpaceWeather(push, observer);
  const { cmes }         = useCmes(push);
  const { reentries }    = useReentry(push);
  const { conjunctions } = useConjunctions(filters.categories.includes('conjunction'), push);

  // Solar briefing + forecast, only polls while dim === 'solar' to save API calls
  const { briefing, forecast, conditions, schumann } = useSolarBriefing(dim === 'solar' || (isMobile && activeTab === 'sky'));

  // Tonight verdict, shared by all four Tonight columns, gated on the mode.
  const { verdict: tonightVerdict, loading: tonightLoading } = useTonight(dim === 'tonight' || (isMobile && activeTab === 'tonight'), observer, tonightActivity);

  // Engagement loop, in-app GO toast + push subscription management
  const { permission: pushPermission, subscribed: pushSubscribed, requestPush,
          unsubscribe: unsubscribePush, error: pushError, pending: pushPending } = useNotification({
    observer, verdict: tonightVerdict, activity: tonightActivity,
  });
  const handlePickSite = useCallback((site) => {
    if (site?.lat != null && site?.lon != null) {
      setObserver({ ...observer, lat: site.lat, lon: site.lon, name: site.name });
    }
  }, [observer, setObserver]);

  const [passClock,setPassClock] = useState(Date.now);
  useEffect(()=>{const timer=setInterval(()=>setPassClock(Date.now()),30000);return()=>clearInterval(timer);},[]);
  const visiblePasses = useMemo(()=>tonightPasses(passes,passClock),[passes,passClock]);
  const activePass = selectTonightPass(visiblePasses,focusedPass);
  // Activity ids (iss/deepsky/lunar/aurora) -> the view mode the center renders by,
  // so 'lunar'->planet chart and iss/aurora->pass dome instead of those ids
  // falling through to an empty dome. tonightActivity stays the id (for the selector).
  const tonightMode = ({ iss: 'sat', aurora: 'aurora', lunar: 'planet', deepsky: 'deepsky', sat: 'sat', planet: 'planet' })[tonightActivity] || 'deepsky';
  // Drop a stale focus when the location changes (the pass list refetches).
  useEffect(() => { setFocusedPass(null); }, [observer?.lat, observer?.lon]);

  useEffect(() => {
    const API = import.meta.env.VITE_API_BASE || '/api';
    let cancelled = false;
    const refresh = () => fetch(`${API}/health`).then(r => r.ok ? r.json() : Promise.reject(new Error('health refresh failed'))).then(d => {
      if (!cancelled) { setDataHealth(d); }
    }).catch(() => { if (!cancelled) setDataHealth(prev => ({...prev,refresh_failed:true})); });
    refresh(); const timer = setInterval(refresh, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  const [cameraMode, setCameraMode] = useState('overview');
  useEffect(()=>{metrics.enter(dim==='tonight'&&observer?tonightCenter:({ '3d':'globe','2d':'lanes' })[dim]||dim);},[dim,tonightCenter,!!observer]);
  useEffect(()=>{if(dim==='solar'){metrics.interactive('solar');if(weather){metrics.usable('solar');metrics.milestone('solar');}}if(dim==='tonight'){metrics.interactive('tonight');if(!observer)metrics.usable('tonight');else if(tonightCenter==='sky'&&!passesLoading&&!passesError){metrics.usable('sky');metrics.milestone('sky');}}if(dim==='events'){metrics.interactive('events');metrics.usable('events');}},[dim,weather,observer,tonightCenter,passesLoading,passesError]);
  useEffect(()=>{if(selected){metrics.start('object-details');metrics.milestone('object-details');if(selected.reentry){metrics.start('reentry');metrics.milestone('reentry');}}},[!!selected,!!selected?.reentry]);
  useEffect(()=>{if(tracked!=null){metrics.start('follow');metrics.milestone('follow');if(cameraMode==='chase'){metrics.start('chase');metrics.milestone('chase');}}},[tracked!=null,cameraMode]);
  useEffect(()=>{if(dim==='tonight'){metrics.start(tonightCenter);if(observer&&!passesLoading&&!passesError){metrics.start('passes');metrics.milestone('passes');}if(passesError){metrics.failure('prediction','passes');metrics.failure('prediction','sky');}}},[dim,tonightCenter,!!observer,passesLoading,passesError]);


  useEffect(()=>{
    const id=Number(selected?.norad_id);
    if(!Number.isInteger(id)||id<=0||selected?.type||['aviation','groundstation','launches'].includes(selected?.category))return;
    let cancelled=false;
    const controller=new AbortController();
    async function refresh(){
      try{
        const response=await fetch((import.meta.env.VITE_API_BASE||'/api')+'/satellites/'+id,{signal:controller.signal});
        const value=await response.json();
        if(cancelled)return;
        if(response.ok&&hasOrbitalPosition(value)){
          setSelected(prior=>Number(prior?.norad_id)===id?{...prior,...value,position_status:'available',position_reason:null}:prior);
        }else{
          const reason=value.position_reason||'Current position could not be refreshed. Camera follow is unavailable.';
          setSelected(prior=>Number(prior?.norad_id)===id?{...prior,lat:null,lon:null,alt_km:null,velocity_kms:null,position_status:value.position_status||'unavailable',position_reason:reason}:prior);
          setTracked(prior=>prior===id?null:prior);setCameraMode('overview');
        }
      }catch(e){
        if(cancelled)return;
        setSelected(prior=>Number(prior?.norad_id)===id?{...prior,position_status:'stale',position_reason:'Current position could not be refreshed. Camera follow is unavailable.'}:prior);
        setTracked(prior=>prior===id?null:prior);setCameraMode('overview');
      }
    }
    refresh();const timer=setInterval(refresh,10000);
    return()=>{cancelled=true;controller.abort();clearInterval(timer);};
  },[selected?.norad_id]);
  const handleSelect   = useCallback((sat) => { selectionGeneration.current++; setSelected(sat); setTracked(null); }, []);
  const handleFollow   = useCallback((sat) => {
    if(!hasOrbitalPosition(sat))return;
    setTracked(sat?.norad_id ?? null);
    if (sat) { selectionGeneration.current++; setSelected(sat); }
    // Following is a scene task on compact screens. Leave Object details
    // available as a peek sheet so the camera modes remain touchable.
    if (viewport.width < 700) setSheetExpanded(false);
  }, [viewport.width]);
  const handleUntrack  = useCallback(() => { setTracked(null); setCameraMode('overview'); }, []);
  const handleVisibleToggle = useCallback(() => {
    setFilters(f => ({ ...f, sunlit_only: !f.sunlit_only, visible_only: !f.visible_only }));
  }, []);

  const handleGoto = useCallback((event) => {
    if(event?.norad_2){metrics.start('conjunctions');metrics.milestone('conjunctions');}
    setDim('3d');
    setActiveTab('selection'); setSheetExpanded(true);
    const generation = ++selectionGeneration.current;
    // Launch object, fly to pad and select the launch marker
    if (event.launch) {
      const l = event.launch;
      const sat = {
        norad_id:     l.id,
        name:         l.name,
        category:     'launches',
        alt_km:       0,
        velocity_kms: 0,
        lat:          l.lat,
        lon:          l.lon,
        launch:       l,
      };
      setSelected(sat);
      setTracked(null);
      // Camera is driven by the `selected` effect in OrbitalGlobe. Setting
      // searchTarget too fired a SECOND, competing flyTo (shallow pitch, 3000 km)
      // that left the pad off the bottom of the frame, that was the bug.
      return;
    }
    if (event.norad_1) {
      setSearchTarget({ norad_id: event.norad_1, name: event.title });
      const API_BASE = import.meta.env.VITE_API_BASE || '/api';
      // Fetch static catalog detail + live propagated position in parallel.
      // /detail has inclination/period/apogee/perigee but NO live alt/velocity;
      // /:norad has the live propagator output (alt_km, velocity_kms).
      Promise.all([
        fetch(`${API_BASE}/satellites/${event.norad_1}/detail`).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API_BASE}/satellites/${event.norad_1}`       ).then(r => r.ok ? r.json() : null).catch(() => null),
      ])
        .then(async ([detail, live]) => {
          const d = { ...(detail || {}), ...(live || {}) };
          if (!d?.norad_id || generation !== selectionGeneration.current) return;
          const reentryRec = reentries.find(r => r.norad_id === d.norad_id);
          if (reentryRec) {
            // Also fetch the latest TIP for richer callout detail in the 2D view
            let tip = null;
            try {
              const tipRes = await fetch(`${API_BASE}/reentry/${d.norad_id}/tip`);
              if (tipRes.ok) {
                const tipData = await tipRes.json();
                tip = tipData.tip || null;
              }
            } catch { /* non-fatal, TIP is optional enrichment */ }
            if (generation !== selectionGeneration.current) return;
            setSelected({ ...d, reentry: { ...reentryRec, tip } });
          } else {
            setSelected(d);
          }
          setTracked(null);
        })
        .catch(() => {});
    } else if (event.lat != null && event.lon != null) {
      setSearchTarget({ lat: event.lat, lon: event.lon });
    }
  }, [reentries]);

  const handleSatSelect = useCallback((sat) => {
    setDim('3d');
    setActiveTab('selection'); setSheetExpanded(true);
    const generation = ++selectionGeneration.current;
    setSearchTarget(sat);
    const reentryRec = reentries.find(r => r.norad_id === sat?.norad_id);
    // Set immediately with what we have so the UI doesn't flash empty.
    setSelected(reentryRec ? { ...sat, reentry: reentryRec } : sat);
    setTracked(null);

    // If the payload is missing live position (alt_km/velocity_kms), fetch
    // detail + live propagator output in the background and merge in. This
    // is the same enrichment handleGoto performs � hoisted here so click
    // paths via search / EventStream / ticker also get the full sat card.
    if (sat?.norad_id && (sat.alt_km == null || sat.velocity_kms == null)) {
      const API_BASE = import.meta.env.VITE_API_BASE || '/api';
      Promise.all([
        fetch(`${API_BASE}/satellites/${sat.norad_id}/detail`).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API_BASE}/satellites/${sat.norad_id}`       ).then(r => r.ok ? r.json() : null).catch(() => null),
      ])
        .then(([detail, live]) => {
          const merged = { ...sat, ...(detail || {}), ...(live || {}) };
          if (!merged.norad_id || generation !== selectionGeneration.current) return;
          setSelected(reentryRec ? { ...merged, reentry: reentryRec } : merged);
        });
    }
  }, [reentries]);

  const handleCommand = useCallback((cmd) => {
    const timeMap = {
      now: 0,
      '+1h': 3600, '+2h': 7200, '+3h': 10800, '+6h': 21600, '+12h': 43200,
      '+24h': 86400, '+48h': 172800, '+72h': 259200,
      '-1h': -3600, '-2h': -7200, '-6h': -21600, '-12h': -43200,
      '-24h': -86400, '-48h': -172800, '-72h': -259200,
    };
    if (cmd in timeMap) { setFilters(f => ({ ...f, time_offset_sec: timeMap[cmd] })); return; }
    if (cmd === 'aurora on')    { setFilters(f => ({ ...f, categories: [...new Set([...f.categories,'aurora'])] })); return; }
    if (cmd === 'aurora off')   { setFilters(f => ({ ...f, categories: f.categories.filter(c => c !== 'aurora') })); return; }
    if (cmd === 'kp') { setDim('solar'); setActiveTab('sky'); setSheetExpanded(false); return; }
    if (cmd === 'aviation on' || cmd === 'aviation off') { setFilters(f => ({...f,categories:cmd.endsWith('on') ? [...new Set([...f.categories,'aviation'])] : f.categories.filter(c => c !== 'aviation')})); return; }
    if (cmd === 'iss') { handleSatSelect({norad_id:25544,name:'ISS (ZARYA)',category:'station'}); return; }
    if (cmd === 'reentry' && reentries.length > 0)
      handleSatSelect({norad_id:reentries[0].norad_id,name:reentries[0].name});
    if (cmd === 'conjunction' && conjunctions.length > 0)
      handleSatSelect({norad_id:conjunctions[0].norad_1,name:conjunctions[0].name_1});
  }, [reentries, conjunctions, handleSatSelect]);

  const handleScrub = useCallback((sec) => {
    setFilters(f => ({ ...f, time_offset_sec: sec }));
  }, []);

  const handleTickerAction = useCallback((action) => {
    if (!action) return;
    if (action.type === 'openSatellite' && action.norad) {
      handleGoto({ norad_1: action.norad, title: 'Ticker selection' });
    } else if (action.type === 'openExternal' && action.url) {
      window.open(action.url, '_blank', 'noopener');
    }
  }, [handleGoto]);

  // Shared links use the same enrichment and selection path as Search.
  const latestSatSelect = useRef(handleSatSelect);
  latestSatSelect.current = handleSatSelect;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const norad = Number(params.get('norad'));
    const generation = selectionGeneration.current;
    const timer = setTimeout(() => {
      if (generation !== selectionGeneration.current) return;
      if (Number.isInteger(norad) && norad > 0) {
        latestSatSelect.current({norad_id:norad,name:`NORAD ${norad}`});
      } else if (params.has('lat') && params.has('lon')) {
        const lat=Number(params.get('lat')),lon=Number(params.get('lon'));
        if(Number.isFinite(lat)&&Number.isFinite(lon)&&Math.abs(lat)<=90&&Math.abs(lon)<=180)setSearchTarget({lat,lon,name:'Shared location'});
      }
    },1500);
    return()=>clearTimeout(timer);
  }, []);

  const globe = (
    <RendererBoundary><OrbitalGlobe
      filters={filters} selected={selected} tracked={tracked}
      observer={observer} searchTarget={searchTarget}
      onSelect={handleSelect} onDetails={(subject,event)=>{panelTrigger.current=event.currentTarget;setSelected(subject);setActiveTab('selection');setSheetExpanded(true);requestAnimationFrame(()=>document.getElementById('workspace-panel')?.focus());}} onUntrack={handleUntrack} onFollow={handleFollow}
      cameraMode={cameraMode} onCameraModeChange={setCameraMode}
      externalConjunctions={conjunctions}
      externalReentries={reentries}
      detailsOpen={activeTab === 'selection' && sheetExpanded}
      selectionRevision={selectionGeneration.current}
    /></RendererBoundary>
  );

  const displayDialogs=<>{displayOpen&&<DisplayPreferences value={display} onChange={v=>{setDisplay(v);}} onClose={()=>setDisplayOpen(false)}/>} {layersOpen&&<PopoutModal title="Layers and guide" onClose={()=>setLayersOpen(false)}><MobilePaneLayers filters={filters} onChange={setFilters}/></PopoutModal>}</>;
  const locationBar = <div className="og-location-bar"><button onClick={() => setLocationOpen(true)} title={observer ? `${observer.name || 'Coordinates'} · ${observer.lat.toFixed(4)}°, ${observer.lon.toFixed(4)}°` : 'Set your observing location'}>
    <span>{observer ? `${observer.name || 'Coordinates'} · ${observer.lat.toFixed(3)}°, ${observer.lon.toFixed(3)}°` : 'Personalize Tonight'}</span><b>{observer ? 'Change' : 'Set location'}</b>
  </button></div>;
  const locationDialog = locationOpen && <LocationDialog observer={observer} onSave={setObserver} onClose={() => setLocationOpen(false)} />;

  const destination = dim === 'solar' || dim === 'tonight' || dim === 'events' ? dim : 'explore';
  const sidePanel = viewport.width >= 700;
  const wideHeader = viewport.width >= 1000;
  const bottomNav = wideHeader ? 0 : 52;
  const contentTop = 48 + (short ? 0 : 44) + alertHeight;
  const panelTitle = ({overview:'Overview',tonight:'Tonight briefing',sky:'Solar briefing',selection:'Object details',layers:'Layers and time',more:'Sky planner',details:destination==='solar'?'Solar details':'Observing details',timeline:'Pass timeline',forecast:'Solar forecast',status:'Space status'})[activeTab] || 'Details';
  const panelOpen = !!activeTab && sheetExpanded && destination !== 'events';
  const pinned = pinnedOverview && viewport.width >= 1440 && destination === 'explore';
  useEffect(()=>{
    if (!panelOpen || !focusPanel.current) return;
    focusPanel.current=false;
    document.getElementById('workspace-panel')?.focus();
  },[panelOpen,activeTab]);
  const closePanel = () => { setSheetExpanded(false); requestAnimationFrame(() => {
    const opener=panelTrigger.current;
    if(opener?.isConnected && opener.getClientRects().length) opener.focus();
    else (toolsTrigger.current || document.querySelector('.og-context-toolbar button'))?.focus();
  }); };
  const openPanel = (id, event) => {
    if (event?.currentTarget) panelTrigger.current = destination === 'explore' && !wideHeader ? toolsTrigger.current : event.currentTarget;
    setExploreToolsOpen(false);
    focusPanel.current=activeTab!==id || !sheetExpanded;
    setActiveTab(id); setSheetExpanded(activeTab === id ? !sheetExpanded : true);
  };
  const navigate = (id) => {
    setExploreToolsOpen(false);
    if (dim === '2d' || dim === '3d') exploreDim.current = dim;
    setDim(id === 'explore' ? exploreDim.current : id);
    setActiveTab(id === 'tonight' ? 'tonight' : id === 'solar' ? 'sky' : selected && id === 'explore' ? 'selection' : null);
    setSheetExpanded(false);
  };
  const panelButton = (id, label) => <button aria-expanded={activeTab === id && panelOpen} aria-controls="workspace-panel" onClick={e=>openPanel(id,e)}>{label}</button>;
  const overview = <LeftConsole observer={observer} setObserver={setObserver} onEditLocation={()=>setLocationOpen(true)} passes={passes} visibleNow={visibleNow} weather={weather} onGoto={handleGoto} visibleOnly={filters.visible_only} onVisibleToggle={handleVisibleToggle}/>;
  const panelContent = <>
    {activeTab === 'overview' && overview}
    {activeTab === 'selection' && <ObjectDetails selected={selected} tracked={tracked} observer={observer} onFollow={handleFollow} onUntrack={handleUntrack} onEditLocation={()=>setLocationOpen(true)} onClose={()=>{selectionGeneration.current++;setSelected(null);setTracked(null);setCameraMode('overview');setActiveTab(null);setSheetExpanded(false);}}/>}
    {activeTab === 'layers' && <MobilePaneLayers filters={filters} onChange={setFilters}/>}
    {activeTab === 'more' && <MobilePaneMore observer={observer} visibleNow={visibleList} selectedNorad={selected?.norad_id??null} onSelectObject={handleSatSelect} onGoto={handleGoto}/>}
    {activeTab === 'status' && <RightConsole observer={observer} weather={weather} conjunctions={conjunctions} reentries={reentries}/>}
    {activeTab === 'tonight' && <MobilePaneTonight observer={observer} onEditLocation={()=>setLocationOpen(true)} pass={activePass} passes={visiblePasses} passLoading={passesLoading} passError={passesError} focusedKey={activePass?`${activePass.norad_id}:${activePass.start}`:null} onFocus={p=>{setFocusedPass(p);setTonightCenter('sky');if(!sidePanel)setSheetExpanded(false);}} onLiveTrack={p=>{handleGoto({norad_1:p.norad_id,title:p.name});setSheetExpanded(false);}} verdict={tonightVerdict} loading={tonightLoading} activity={tonightActivity} onActivity={setTonightActivity} onPickSite={handlePickSite} centerView={tonightCenter} onCenterView={v=>{setTonightCenter(v);setSheetExpanded(false);}} hideCenter onSolar={()=>navigate('solar')} onGoto={handleGoto}/>}
    {activeTab === 'sky' && <SolarBriefing mobile={!sidePanel} health={dataHealth} weather={weather} briefing={briefing} conditions={conditions} schumann={schumann} cmes={cmes} onSelectEvent={e=>{setSolarEvent(e);setActiveTab('details');}}/>}
    {activeTab === 'details' && (destination==='solar' ? <SolarRightPanel weather={weather} cmes={cmes} reentries={reentries} forecast={forecast} selectedEvent={solarEvent} onSelectEvent={setSolarEvent} onClose={()=>setSolarEvent(null)}/> : <TonightDetails observer={observer} verdict={tonightVerdict} loading={tonightLoading} pass={activePass} onPickSite={handlePickSite} onActivity={setTonightActivity}/>)}
    {activeTab === 'timeline' && <TonightStrip passes={visiblePasses} loading={passesLoading} observer={observer} verdict={tonightVerdict} focusedKey={activePass?`${activePass.norad_id}:${activePass.start}`:null} onFocus={setFocusedPass} onGoto={handleGoto}/>}
    {activeTab === 'forecast' && <SolarForecastStrip weather={weather} forecast={forecast} cmes={cmes} onSelectEvent={e=>{setSolarEvent(e);setActiveTab('details');}}/>}
  </>;
  const exploreTools = <>{panelButton('overview','Overview')}{panelButton('layers','Layers & time')}{panelButton('more','Sky planner')}
          {selected && panelButton('selection','Object details')}
          {panelButton('status','Space status')}
          {viewport.width>=1440 && <button aria-pressed={pinnedOverview} onClick={()=>{setPinnedOverview(v=>!v);if(activeTab==='overview')setSheetExpanded(false);}}>Pin overview</button>}</>;
  return <div className={`og-shell og-unified-shell ${isMobile?'og-compact-shell':'og-desktop-shell'} v2-dim-${dim}`} style={{...ROOT,'--shell-bottom':`${bottomNav}px`,'--og-camera-controls-top':'12px'}}>
    <a className="og-skip-link" href="#workspace" onClick={e=>{e.preventDefault();document.getElementById("workspace")?.focus();}}>Skip to workspace</a>
    {locationDialog}{displayDialogs}
    <div className="og-shell-header">
      <MobileTopBar onOpenLocation={()=>setLocationOpen(true)} onOpenSearch={()=>setPaletteOpenCount(c=>c+1)} onOpenDisplay={()=>setDisplayOpen(true)} push={{permission:pushPermission,subscribed:pushSubscribed,error:pushError,pending:pushPending,requestPush,unsubscribe:unsubscribePush}}>
        {wideHeader && <DestinationNav current={destination} onSelect={navigate}/>}
      </MobileTopBar>
    </div>
    {!short && <div className="og-shell-location">{locationBar}</div>}
    <div style={{position:'absolute',top:short?48:92,left:0,right:0,zIndex:50}}><AlertToast onGoto={handleGoto} onHeight={setAlertHeight}/></div>
    <main id="workspace" tabIndex={-1} aria-label={`${destination} workspace`} className="og-workspace" style={{top:contentTop}}>
      <div className="og-context-toolbar" aria-label={`${destination} tools`}>
        {destination==='explore' && <>
          <div className="og-view-switch" role="group" aria-label="Explore view">
            <button aria-pressed={dim==='3d'} onClick={()=>{setDim('3d');exploreDim.current='3d';}}>3D Globe</button>
            <button aria-pressed={dim==='2d'} onClick={()=>{setDim('2d');exploreDim.current='2d';}}>2D Lanes</button>
          </div>
          {wideHeader ? exploreTools : <button ref={toolsTrigger} aria-expanded={exploreToolsOpen} aria-controls="explore-tools" onClick={()=>setExploreToolsOpen(v=>!v)}>Tools</button>}
        </>}
        {destination==='tonight' && <>{panelButton('tonight','Briefing')}<div className="og-view-switch" role="group" aria-label="Tonight view">{['sky','map','gear'].map(v=><button key={v} aria-pressed={tonightCenter===v} onClick={()=>setTonightCenter(v)}>{({sky:'Sky',map:'Map',gear:'Gear'})[v]}</button>)}</div>{panelButton('details','Observing details')}{panelButton('timeline','Pass timeline')}</>}
        {destination==='solar' && <>{panelButton('sky','Briefing')}{panelButton('details','Key moments')}{panelButton('forecast','Forecast')}</>}
        {destination==='events' && <h1>Events</h1>}
      </div>
      {exploreToolsOpen && destination==='explore' && !wideHeader && <div id="explore-tools" className="og-explore-tools" onKeyDown={e=>{if(e.key==='Escape'){setExploreToolsOpen(false);toolsTrigger.current?.focus();}}}>
        <div className="og-panel-heading"><h2>Explore tools</h2><button onClick={()=>{setExploreToolsOpen(false);toolsTrigger.current?.focus();}}>Close</button></div>{exploreTools}
      </div>}
      <div className="og-workspace-body">
        {pinned && <aside className="og-pinned-overview" aria-label="Pinned overview">{overview}</aside>}
        <div className="og-primary-view" aria-label={destination==='explore'?'Orbital scene. Use Search or Sky planner for keyboard object selection.':destination==='tonight'?"Tonight's sky":destination==='solar'?'Sun-to-Earth space weather':'Events'}>
          {dim==='3d' && globe}
          {dim==='2d' && <OrbitsView observer={observer} filters={filters} selected={selected} onSelect={handleSelect}/>}
          {dim==='solar' && <SolarHero weather={weather} cmes={cmes}/>}
          {dim==='tonight' && (!observer ? <div className="og-location-prompt"><h2>Plan your observing night</h2><p>Set your location to see local passes, sky conditions and nearby observing sites.</p><button className="og-primary" onClick={()=>setLocationOpen(true)}>Set location</button></div> : tonightCenter==='map' ? <TonightMap mobile={!sidePanel} observer={observer} onPickSite={handlePickSite}/> : tonightCenter==='gear' ? <div className="og-scrolling-scene"><GearRecipe mobile={!sidePanel} observer={observer} verdict={tonightVerdict} activity={tonightMode} items={[]}/></div> : tonightMode==='aurora' ? <AuroraOutlook observer={observer} verdict={tonightVerdict} weather={weather} onSolar={()=>navigate('solar')}/> : (tonightMode==='planet'||tonightMode==='deepsky') ? <SkyPlotView observer={observer} activity={tonightMode}/> : (passesLoading||passesError) ? <div className="og-location-prompt" role="status">{passesLoading?'Loading local passes…':'Pass predictions are unavailable. Try again shortly.'}</div> : <PassPlot pass={activePass} observer={observer} ghostPasses={visiblePasses.filter(p=>activePass?`${p.norad_id}:${p.start}`!==`${activePass.norad_id}:${activePass.start}`:true).slice(0,2)} onLiveTrack={p=>handleGoto({norad_1:p.norad_id,title:p.name})}/>)}
          {dim==='events' && <div className="og-events-page"><Ticker observer={observer} onAction={handleTickerAction} height={30}/><MobilePaneEvents filters={eventFilters} onFilters={setEventFilters} onGoto={handleGoto} onLaunch={l=>handleGoto({launch:l})}/><details className="og-event-timeline"><summary>Launch timeline</summary><LaunchTimeline onGoto={handleGoto}/></details></div>}
        </div>
        {sidePanel && panelOpen && <aside id="workspace-panel" tabIndex={-1} className="og-context-panel" aria-label={panelTitle} onKeyDown={e=>{if(e.key==='Escape'&&!document.querySelector('[aria-modal="true"]'))closePanel();}}>
          <div className="og-panel-heading"><h2>{panelTitle}</h2><button onClick={closePanel} aria-label={`Close ${panelTitle}`}>Close</button></div>
          <div className={`og-panel-body og-panel-${activeTab}`}>{panelContent}</div>
        </aside>}
      </div>
    </main>
    {!sidePanel && activeTab && destination!=='events' && <MobileSheet title={panelTitle} expanded={sheetExpanded} onToggle={v=>v?setSheetExpanded(true):closePanel()} tabBarHeightPx={bottomNav} contentTopPx={contentTop+52} short><div id="workspace-panel" tabIndex={-1} role="region" aria-label={panelTitle} className={`og-sheet-content og-panel-${activeTab}`}>{panelContent}</div></MobileSheet>}
    {!wideHeader && <div className="og-destination-bottom"><DestinationNav current={destination} onSelect={navigate}/></div>}
    <div className="og-sr-only" aria-live="polite">{selected?`Selected ${selected.name||selected.data?.name||'object'}`:''}</div>
    <CommandPalette externalOpen={paletteOpenCount} onCommand={handleCommand} onSatSelect={handleSatSelect}/>
  </div>;
}

export default function App() {
  return (
    <EventBusProvider>
      <AppInner />
    </EventBusProvider>
  );
}
